import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FileUpload } from 'primeng/fileupload';
import { Location } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService, SelectItem } from 'primeng/api';

import { BaseServices } from '../../kuba.services';
import { SdsArchiveFile } from './sds-archive-picker.component';
import { SdsHazardCategory, SdsSearchSuggestion, SdsSupplierName, SdsSupplierOption } from '../models/sds.model';
import { nationalIdentityNumberValidator } from '../validators/national-identity-number.validator';
import {
  casListValidator,
  joinCasNumbers,
  mergeCasNumbersWithinLimit,
  splitCasNumbers
} from '../validators/cas-number.validator';
import { SdsPdfScannerService, SdsPdfScanResult } from '../services/sds-pdf-scanner.service';
import {
  DEFAULT_SDS_UI_PICTOGRAMS,
  SdsApiService,
  SdsCreateDocumentRequest,
  SdsEligibleEditor,
  SdsHazardStatementDto,
  SdsUiPictogramOption
} from '../services/sds-api.service';

/** Translation keys are resolved at runtime via TranslateService. */
const HAZARD_CATEGORY_OPTION_KEYS: { labelKey: string; value: SdsHazardCategory; }[] = [
  { labelKey: 'SDS_HAZARD_CATEGORY_NONE', value: SdsHazardCategory.None },
  { labelKey: 'SDS_HAZARD_CATEGORY_ASBESTOS', value: SdsHazardCategory.Asbestos },
  { labelKey: 'SDS_HAZARD_CATEGORY_CARCINOGENIC', value: SdsHazardCategory.Carcinogenic },
  { labelKey: 'SDS_HAZARD_CATEGORY_MUTAGENIC', value: SdsHazardCategory.Mutagenic },
  { labelKey: 'SDS_HAZARD_CATEGORY_REPROTOXIC', value: SdsHazardCategory.Reprotoxic },
  { labelKey: 'SDS_HAZARD_CATEGORY_LEAD', value: SdsHazardCategory.Lead },
  { labelKey: 'SDS_HAZARD_CATEGORY_BIOLOGICAL_AGENT', value: SdsHazardCategory.BiologicalAgent },
  { labelKey: 'SDS_HAZARD_CATEGORY_IONISING_RADIATION', value: SdsHazardCategory.IonisingRadiation },
  { labelKey: 'SDS_HAZARD_CATEGORY_OTHER_REGULATED', value: SdsHazardCategory.OtherRegulated }
];

@Component({
  selector: 'sds-new',
  templateUrl: 'sds-new.component.html',
  styleUrls: ['sds-new.component.css', './sds-theme.css'],
  providers: [],
  standalone: false
})
export class SdsNewComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  currentStep = 0;
  steps: { label: string }[] = [];
  private readonly baseStepKeys = ['SDS_STEP_PRODUCT_INFO', 'SDS_STEP_HAZARD_USAGE', 'SDS_STEP_CONTROLS_RISK'];
  private readonly exposureStepKey = 'SDS_STEP_EXPOSURE_LOG';
  private stepKeys = [...this.baseStepKeys];

  step1Form!: FormGroup;
  step2Form!: FormGroup;
  step3Form!: FormGroup;
  exposureForm!: FormGroup;

  /** Temporarily hides the "search by product name" bar (feature parked for now). */
  showSdsSearch = false;
  sdsSearchQuery = '';
  sdsSearchResults: SdsSearchSuggestion[] = [];
  isSearching = false;
  isImportingSearchResult = false;
  private searchRequestId = 0;

  @ViewChild('sdsFileUpload') sdsFileUpload?: FileUpload;

  uploadedFile: File | null = null;
  uploadedFileName = '';

  /** True while a just-uploaded PDF is being scanned for a CAS number. */
  scanningCasFromPdf = false;

  /** Business whose archive is browsed in the "add from archive" picker. */
  businessId = BaseServices.BusinessId;
  archivePickerVisible = false;
  selectedArchiveFile: SdsArchiveFile | null = null;

  suppliers: SdsSupplierOption[] = [];
  addSupplierVisible = false;
  newSupplierName = '';

  selectedPictograms: string[] = [];
  pictogramList: SdsUiPictogramOption[] = DEFAULT_SDS_UI_PICTOGRAMS.map(option => ({ ...option }));
  private pictogramCatalogLoaded = false;

  figureValue: SelectItem[] = [];
  probabilityValue = 1;
  consequenceValue = 1;
  riskData: any = { Id: 0, value: '', backColor: '' };

  loading = false;
  canAccessEmployeeExposureData = false;

  /** Active business employees offered in the exposure picker. */
  employeeOptions: SelectItem[] = [];
  private employeesById = new Map<number, SdsEligibleEditor>();

  /** When set (via ?replaces=), the created SDS is linked as this chemical's replacement. */
  replacesDocumentId: number | null = null;

  hazardCategoryOptions: SelectItem[] = HAZARD_CATEGORY_OPTION_KEYS.map(o => ({
    label: o.labelKey,
    value: o.value
  }));
  readonly HazardCategory = SdsHazardCategory;

  pdfPreviewVisible = false;
  pdfPreviewLoading = false;
  pdfPreviewUrl: SafeResourceUrl | null = null;
  private pdfBlobUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private sdsApiService: SdsApiService,
    private pdfScanner: SdsPdfScannerService
  ) {}

  ngOnInit(): void {
    document.querySelector('body')!.classList.remove('opened');
    const replaces = this.route.snapshot.queryParamMap.get('replaces');
    this.replacesDocumentId = replaces ? Number(replaces) : null;
    this.initForms();
    this.initDropdowns();
    this.setRiskFigure(1, 1);
    this.loadStepLabels();
    this.loadSuppliers();
    this.loadPictograms();
    this.loadExposureAccessPermission();

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.suppliers = this.suppliers.map(option => option.value === null
        ? { ...option, label: this.translate.instant('SDS_SELECT_MANUFACTURER') }
        : option);
      this.loadStepLabels();
      this.refreshHazardCategoryLabels();
    });
    this.refreshHazardCategoryLabels();
  }

  private refreshHazardCategoryLabels(): void {
    const keys = HAZARD_CATEGORY_OPTION_KEYS.map(o => o.labelKey);
    this.translate.get(keys).subscribe(translations => {
      this.hazardCategoryOptions = HAZARD_CATEGORY_OPTION_KEYS.map(o => ({
        label: translations[o.labelKey] || o.labelKey,
        value: o.value
      }));
    });
  }

  private loadStepLabels(): void {
    this.translate.get(this.stepKeys).subscribe(translations => {
      this.steps = this.stepKeys.map(key => ({ label: translations[key] }));
    });
  }

  private initForms(): void {
    this.step1Form = this.fb.group({
      ProductName: ['', Validators.required],
      Supplier: [null, Validators.required],
      CASNumbers: [<string[]>[], [Validators.required, casListValidator]],
      NoCasNumber: [false],
      SdsFile: [null]
    });

    this.step1Form.get('NoCasNumber')!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(noCasNumber => {
      const casNumbers = this.step1Form.get('CASNumbers')!;
      if (noCasNumber === true) {
        casNumbers.disable();
      } else {
        casNumbers.enable();
      }
    });

    this.step2Form = this.fb.group({
      HazardStatements: [<SdsHazardStatementDto[]>[]],
      Description: [''],
      KeyRisk: [''],
      TaskActivity: [''],
      RequiresExposureTracking: [false],
      HazardCategory: [SdsHazardCategory.None]
    });

    this.step3Form = this.fb.group({
      OtherControls: [''],
      Probability: [1],
      Consequence: [1]
    });

    this.exposureForm = this.fb.group({
      EmployeeExposures: this.fb.array([])
    });

    this.step2Form.get('RequiresExposureTracking')?.valueChanges.subscribe(requiresTracking => {
      if (requiresTracking === true &&
          this.canAccessEmployeeExposureData &&
          this.employeeExposures.length === 0) {
        this.addEmployeeExposure();
      }

      this.refreshStepKeys();
    });
  }

  private initDropdowns(): void {
    this.suppliers = this.buildSupplierOptions([]);

    this.figureValue = [
      { label: '1', value: 1 },
      { label: '2', value: 2 },
      { label: '3', value: 3 },
      { label: '4', value: 4 },
      { label: '5', value: 5 }
    ];
  }

  get employeeExposures(): FormArray {
    return this.exposureForm.get('EmployeeExposures') as FormArray;
  }

  get requiresExposureTracking(): boolean {
    return this.step2Form?.get('RequiresExposureTracking')?.value === true;
  }

  get finalStepIndex(): number {
    return this.steps.length - 1;
  }

  /** Progress-line fill: 0% on the first step, 100% on the last. */
  get stepProgressPercent(): number {
    const lastIndex = this.steps.length - 1;
    return lastIndex > 0 ? (this.currentStep / lastIndex) * 100 : 0;
  }

  private refreshStepKeys(): void {
    this.stepKeys = this.requiresExposureTracking
      ? [this.baseStepKeys[0], this.baseStepKeys[1], this.exposureStepKey, this.baseStepKeys[2]]
      : [...this.baseStepKeys];

    if (this.currentStep >= this.stepKeys.length) {
      this.currentStep = this.stepKeys.length - 1;
    }

    this.loadStepLabels();
  }

  private loadSuppliers(): void {
    this.sdsApiService.getCurrentBusinessSupplierNames().subscribe({
      next: names => {
        this.suppliers = this.buildSupplierOptions(names);
      },
      error: error => {
        this.suppliers = this.buildSupplierOptions([]);

        if (error?.status !== 404) {
          this.messageService.add({
            severity: 'warn',
            summary: 'SDS',
            detail: this.translate.instant('SDS_MANUFACTURERS_LOAD_ERROR')
          });
        }
      }
    });
  }

  private buildSupplierOptions(names: SdsSupplierName[]): SdsSupplierOption[] {
    const byName = new Map<string, SdsSupplierOption>();

    for (const entry of names || []) {
      const name = (entry?.Name || '').trim();
      if (!name || byName.has(name.toLowerCase())) {
        continue;
      }

      byName.set(name.toLowerCase(), { label: name, value: name, isDefault: !!entry.IsDefault });
    }

    return [
      { label: this.translate.instant('SDS_SELECT_MANUFACTURER'), value: null, isDefault: false },
      ...[...byName.values()].sort((left, right) => left.label.localeCompare(right.label))
    ];
  }

  private ensureSupplierOption(name: string | null | undefined): void {
    const normalizedName = (name || '').trim();
    if (!normalizedName) {
      return;
    }

    const exists = this.suppliers.some(
      supplier => String(supplier.value || '').toLowerCase() === normalizedName.toLowerCase()
    );
    if (exists) {
      return;
    }

    this.suppliers = this.buildSupplierOptions([
      ...this.suppliers
        .filter(supplier => !!supplier.value)
        .map(supplier => ({ Name: String(supplier.value), IsDefault: supplier.isDefault })),
      { Name: normalizedName, IsDefault: false }
    ]);
  }

  private loadPictograms(): void {
    this.sdsApiService.getPictogramOptions().subscribe({
      next: options => {
        this.pictogramList = options;
        this.pictogramCatalogLoaded = true;
      },
      error: error => {
        this.pictogramCatalogLoaded = false;

        if (error?.status !== 404) {
          this.messageService.add({
            severity: 'warn',
            summary: 'SDS',
            detail: 'Could not load pictograms from the backend. The fallback icons are shown until the API responds.'
          });
        }
      }
    });
  }

  private loadExposureAccessPermission(): void {
    this.sdsApiService.getMyExposureAccess(BaseServices.BusinessId).subscribe({
      next: permission => {
        this.canAccessEmployeeExposureData =
          permission?.CanAccessEmployeeExposureData === true;

        if (!this.canAccessEmployeeExposureData) {
          this.employeeExposures.clear();
          this.refreshStepKeys();
        } else {
          this.loadBusinessEmployees();
          if (this.requiresExposureTracking && this.employeeExposures.length === 0) {
            this.addEmployeeExposure();
          }
        }
      },
      error: () => {
        this.canAccessEmployeeExposureData = false;
        this.employeeExposures.clear();
        this.refreshStepKeys();
      }
    });
  }

  /** Load active business employees so the exposure picker can auto-fill name/email. */
  private loadBusinessEmployees(): void {
    if (this.employeeOptions.length > 0) {
      return;
    }
    this.sdsApiService.getBusinessEmployees(BaseServices.BusinessId).subscribe({
      next: employees => {
        this.employeesById = new Map((employees || []).map(e => [e.UserId, e]));
        this.employeeOptions = (employees || []).map(e => ({
          label: e.Email ? `${e.Name} (${e.Email})` : e.Name,
          value: e.UserId
        }));
      },
      error: () => {
        this.employeeOptions = [];
        this.employeesById = new Map();
      }
    });
  }

  /**
   * When an employee is picked from the dropdown, auto-fill the free-text
   * name/email fields. The national identity number is deliberately left blank —
   * it is sensitive PII and must be entered manually.
   */
  onEmployeeSelected(index: number, userId: number | null): void {
    const group = this.employeeExposures.at(index) as FormGroup;
    if (!group) {
      return;
    }

    const employee = userId != null ? this.employeesById.get(userId) : undefined;
    if (!employee) {
      return;
    }

    const { firstName, lastName } = this.splitEmployeeName(employee.Name);
    group.patchValue({
      FirstName: firstName,
      LastName: lastName,
      Email: employee.Email ?? ''
    });
  }

  /** Split a full display name into first name (first token) and last name (remainder). */
  private splitEmployeeName(fullName: string | null | undefined): { firstName: string; lastName: string; } {
    const name = (fullName ?? '').trim();
    if (!name) {
      return { firstName: '', lastName: '' };
    }
    const firstSpace = name.indexOf(' ');
    if (firstSpace < 0) {
      return { firstName: name, lastName: '' };
    }
    return {
      firstName: name.slice(0, firstSpace).trim(),
      lastName: name.slice(firstSpace + 1).trim()
    };
  }

  searchSds(): void {
    const query = this.sdsSearchQuery.trim();
    if (query.length < 2) {
      this.searchRequestId++;
      this.sdsSearchResults = [];
      this.isSearching = false;
      return;
    }

    const requestId = ++this.searchRequestId;
    this.isSearching = true;

    this.sdsApiService.searchBusinessDocumentSuggestions(BaseServices.BusinessId, query).subscribe({
      next: results => {
        if (requestId !== this.searchRequestId) {
          return;
        }

        this.sdsSearchResults = results;
        this.isSearching = false;
      },
      error: error => {
        if (requestId !== this.searchRequestId) {
          return;
        }

        this.sdsSearchResults = [];
        this.isSearching = false;

        if (error?.status !== 404) {
          this.messageService.add({
            severity: 'warn',
            summary: 'SDS',
            detail: 'Could not search safety data sheets from the backend.'
          });
        }
      }
    });
  }

  async selectSdsResult(result: SdsSearchSuggestion): Promise<void> {
    this.ensureSupplierOption(result.supplier);
    this.step1Form.patchValue({
      ProductName: result.productName,
      Supplier: result.supplier,
      CASNumbers: result.casNumber ? splitCasNumbers(result.casNumber) : [],
      NoCasNumber: false
    });

    this.uploadedFile = null;
    this.uploadedFileName = '';
    this.step1Form.patchValue({ SdsFile: null });
    this.revokePdfBlobUrl();
    this.sdsSearchResults = [];
    this.sdsSearchQuery = result.productName;

    if (!result.fileUrl) {
      return;
    }

    try {
      this.isImportingSearchResult = true;
      const importedFile = await this.sdsApiService
        .downloadDocumentFile(result.fileUrl, result.fileName, result.fileMime)
        .toPromise();

      if (!importedFile) {
        throw new Error('The selected SDS file could not be downloaded.');
      }

      this.uploadedFile = importedFile;
      this.uploadedFileName = importedFile.name;
      this.step1Form.patchValue({ SdsFile: importedFile });
    } catch (_) {
      this.messageService.add({
        severity: 'warn',
        summary: 'SDS',
        detail: 'The SDS details were loaded, but the file could not be imported automatically. Upload the file manually.'
      });
    } finally {
      this.isImportingSearchResult = false;
    }
  }

  onFileSelect(event: any): void {
    if (event.files && event.files.length > 0) {
      const file = event.files[0];
      this.uploadedFile = file;
      this.uploadedFileName = file.name;
      // A directly uploaded file supersedes any previously chosen archive file.
      this.selectedArchiveFile = null;
      this.step1Form.patchValue({
        ProductName: this.getRecordNameFromFileName(file.name),
        SdsFile: file
      });
      this.scanPdfForCas(file);
    }
  }

  onFileClear(): void {
    this.uploadedFile = null;
    this.uploadedFileName = '';
    this.selectedArchiveFile = null;
    this.step1Form.patchValue({ SdsFile: null });
    this.revokePdfBlobUrl();
    // Reset PrimeNG's own state too — in basic mode it keeps the chosen file on
    // its button, so without this the file appears un-removable. clear() also
    // resets the native input so re-selecting the same file fires (onSelect).
    this.sdsFileUpload?.clear();
  }

  openArchivePicker(): void {
    this.archivePickerVisible = true;
  }

  async onArchiveFileSelected(file: SdsArchiveFile): Promise<void> {
    // An archive selection and a direct upload are mutually exclusive.
    this.selectedArchiveFile = file;
    this.uploadedFile = null;
    this.uploadedFileName = file.name;
    this.revokePdfBlobUrl();
    this.step1Form.patchValue({
      ProductName: this.getRecordNameFromFileName(file.name),
      SdsFile: null
    });

    // Scan the archive PDF for CAS numbers too — same behaviour as a PC upload.
    try {
      const pdf = await this.sdsApiService
        .getArchivePdfPreviewFile(this.businessId, file.id, file.name)
        .toPromise();
      if (pdf) {
        this.scanPdfForCas(pdf);
      }
    } catch (_) {
      // Non-fatal: the user can still enter the CAS number manually.
    }
  }

  /**
   * Scan a freshly uploaded PDF for CAS numbers and either auto-fill the CAS
   * field, offer a picker (multiple matches / a value already present), or show
   * an info toast when nothing usable was found.
   */
  private async scanPdfForCas(file: File): Promise<void> {
    if (!this.pdfScanner.isPdf(file)) {
      return;
    }

    this.scanningCasFromPdf = true;
    try {
      const data = await this.pdfScanner.scanDocument(file);
      this.applyCasScanResult(data.cas);
      this.applyHStatementCodes(data.hStatementCodes);
    } finally {
      this.scanningCasFromPdf = false;
    }
  }

  private applyCasScanResult(result: SdsPdfScanResult): void {
    const { candidates, outcome } = result;
    if (candidates.length === 0) {
      // Distinguish "couldn't read text" (scanned image / unreadable) from
      // "read the PDF but it has no CAS number".
      this.messageService.add({
        severity: outcome === 'no-text' ? 'warn' : 'info',
        summary: 'SDS',
        detail: this.translate.instant(outcome === 'no-text' ? 'SDS_CAS_NO_TEXT' : 'SDS_CAS_NONE')
      });
      return;
    }

    // A document can hold several CAS numbers — add every valid one found,
    // de-duplicated and keeping anything the user already entered.
    const { added, skipped } = this.mergeCasNumbers(candidates);

    if (added.length > 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'SDS',
        detail: added.length === 1
          ? this.translate.instant('SDS_CAS_DETECTED', { cas: added[0] })
          : this.translate.instant('SDS_CAS_MULTIPLE_ADDED', { count: added.length })
      });
    }

    if (skipped > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'SDS',
        detail: this.translate.instant('SDS_CAS_TOO_MANY_SKIPPED', { count: skipped })
      });
    }
  }

  /**
   * Adds the scanned CAS numbers to the chips control, skipping duplicates and
   * anything that would push the joined list past the length the API stores.
   * Returns the numbers actually added and how many were left out.
   */
  private mergeCasNumbers(found: string[]): { added: string[]; skipped: number; } {
    if (this.step1Form.get('NoCasNumber')?.value === true) {
      return { added: [], skipped: 0 };
    }
    const control = this.step1Form.get('CASNumbers');
    const existing: string[] = Array.isArray(control?.value) ? control!.value : [];
    const { merged, added, skipped } = mergeCasNumbersWithinLimit(existing, found);

    control?.setValue(merged);
    control?.markAsDirty();
    control?.markAsTouched();
    return { added, skipped: skipped.length };
  }

  /** Resolve H/EUH codes scanned from the PDF and add them to the hazard-statement picker. */
  private applyHStatementCodes(codes: string[]): void {
    if (!codes.length) {
      return;
    }
    this.sdsApiService.resolveHazardStatementsByCodes(codes).subscribe(statements => {
      if (!statements.length) {
        return;
      }
      this.mergeHazardStatements(statements);
      this.messageService.add({
        severity: 'success',
        summary: 'SDS',
        detail: this.translate.instant('SDS_HSTATEMENT_DETECTED', { count: statements.length })
      });
    });
  }

  private mergeHazardStatements(found: SdsHazardStatementDto[]): void {
    const control = this.step2Form.get('HazardStatements');
    const existing: SdsHazardStatementDto[] = Array.isArray(control?.value) ? control!.value : [];
    const seen = new Set(existing.map(s => s.Id));
    const merged = [...existing];
    for (const statement of found) {
      if (!seen.has(statement.Id)) {
        merged.push(statement);
        seen.add(statement.Id);
      }
    }
    control?.setValue(merged);
    control?.markAsDirty();
    control?.markAsTouched();
  }

  async openPdfPreview(): Promise<void> {
    // Directly-uploaded file: preview the local blob immediately.
    if (this.uploadedFile) {
      this.revokePdfBlobUrl();
      this.pdfBlobUrl = URL.createObjectURL(this.uploadedFile);
      this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.pdfBlobUrl}#zoom=100`);
      this.pdfPreviewVisible = true;
      return;
    }

    // Archive-selected file: fetch the bytes from the backend, then preview.
    if (this.selectedArchiveFile) {
      this.revokePdfBlobUrl();
      this.pdfPreviewVisible = true;
      this.pdfPreviewLoading = true;
      try {
        const file = await this.sdsApiService
          .getArchivePdfPreviewFile(
            this.businessId,
            this.selectedArchiveFile.id,
            this.selectedArchiveFile.name
          )
          .toPromise();
        if (file) {
          this.pdfBlobUrl = URL.createObjectURL(file);
          this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.pdfBlobUrl}#zoom=100`);
        }
      } catch (_) {
        this.messageService.add({
          severity: 'error',
          summary: 'SDS',
          detail: this.translate.instant('SDS_PDF_NOT_AVAILABLE')
        });
      } finally {
        this.pdfPreviewLoading = false;
      }
    }
  }

  closePdfPreview(): void {
    this.pdfPreviewVisible = false;
    this.pdfPreviewLoading = false;
  }

  get hasPdfFile(): boolean {
    return !!this.uploadedFile || !!this.selectedArchiveFile;
  }

  private revokePdfBlobUrl(): void {
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
      this.pdfPreviewUrl = null;
    }
  }

  private getRecordNameFromFileName(fileName: string | null | undefined): string {
    return this.pdfScanner.getProductName(fileName);
  }

  ngOnDestroy(): void {
    this.revokePdfBlobUrl();
  }

  openAddSupplier(): void {
    this.addSupplierVisible = true;
    this.newSupplierName = '';
  }

  saveNewSupplier(): void {
    if (this.newSupplierName.trim()) {
      const exists = this.suppliers.find(
        supplier => supplier.label?.toLowerCase() === this.newSupplierName.trim().toLowerCase()
      );

      if (!exists) {
        this.ensureSupplierOption(this.newSupplierName);
      }

      this.step1Form.patchValue({ Supplier: this.newSupplierName.trim() });
      this.addSupplierVisible = false;
      this.newSupplierName = '';
    }
  }

  togglePictogram(id: string): void {
    const index = this.selectedPictograms.indexOf(id);
    if (index > -1) {
      this.selectedPictograms.splice(index, 1);
    } else {
      this.selectedPictograms.push(id);
    }
  }

  isPictogramSelected(id: string): boolean {
    return this.selectedPictograms.includes(id);
  }

  nextStep(): void {
    if (this.currentStep === 0 && !this.isStep1Valid()) {
      return;
    }

    if (this.currentStep === 1 && !this.isStep2Valid()) {
      return;
    }

    if (this.requiresExposureTracking && this.currentStep === 2 && !this.isExposureStepValid()) {
      return;
    }

    if (this.currentStep < this.finalStepIndex) {
      this.currentStep++;
    }
  }

  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  goToStep(index: number): void {
    if (index <= this.currentStep) {
      this.currentStep = index;
    } else if (index === this.currentStep + 1) {
      this.nextStep();
    }
  }

  /**
   * Pictograms are deliberately optional: not every chemical registered here is
   * classified as hazardous, so a data sheet may carry no GHS pictogram at all.
   */
  isStep1Valid(): boolean {
    return this.step1Form.valid;
  }

  isStep2Valid(): boolean {
    return this.step2Form.valid;
  }

  isStep3Valid(): boolean {
    return this.step3Form.valid;
  }

  isExposureStepValid(): boolean {
    return ( !this.requiresExposureTracking ||
      !this.canAccessEmployeeExposureData ||
      (this.employeeExposures.length > 0 && this.exposureForm.valid));
  }

  addEmployeeExposure(): void {
    this.employeeExposures.push(this.createEmployeeExposureGroup());
  }

  removeEmployeeExposure(index: number): void {
    this.employeeExposures.removeAt(index);
  }

  private createEmployeeExposureGroup(): FormGroup {
    return this.fb.group({
      Id: [null],
      EmployeeUserId: [null],
      FirstName: [''],
      LastName: [''],
      Email: ['', Validators.email],
      Address: [''],
      // Plaintext NIN; server encrypts and stores ciphertext + last-4 only.
      // "Required" is enforced server-side by hazard category. Format/checksum
      // validator runs on every keystroke so the user gets immediate feedback.
      NationalIdentityNumber: ['', [nationalIdentityNumberValidator]],
      MedicalExamDate: [null],
      MedicalExamDoctorName: [''],
      ExposureStartDate: [null, Validators.required],
      ExposureEndDate: [null]
    });
  }

  get hazardCategoryValue(): SdsHazardCategory {
    return (this.step2Form?.get('HazardCategory')?.value ?? SdsHazardCategory.None) as SdsHazardCategory;
  }

  get isAsbestos(): boolean {
    return this.hazardCategoryValue === SdsHazardCategory.Asbestos;
  }

  get requiresNationalIdentityNumber(): boolean {
    return this.hazardCategoryValue !== SdsHazardCategory.None;
  }

  probabilityChanged(event: any): void {
    this.probabilityValue = event.value;
    this.setRiskFigure(this.probabilityValue, this.consequenceValue);
  }

  consequenceChanged(event: any): void {
    this.consequenceValue = event.value;
    this.setRiskFigure(this.probabilityValue, this.consequenceValue);
  }

  setRiskFigure(probability: any, consequence: any): void {
    if (probability && consequence) {
      const totalFigure = parseInt(probability, 10) * parseInt(consequence, 10);

      if (totalFigure <= 6) {
        this.riskData = { Id: 1, value: 'LOW', backColor: '#4ec55c' };
      } else if (totalFigure > 14) {
        this.riskData = { Id: 3, value: 'HIGH', backColor: '#e7644f' };
      } else {
        this.riskData = { Id: 2, value: 'MEDIUM', backColor: '#f0ac19' };
      }
    }
  }

  async saveRecord(): Promise<void> {
    if (!this.isStep1Valid() || !this.isStep2Valid() || !this.isExposureStepValid() || !this.isStep3Valid()) {
      this.exposureForm.markAllAsTouched();
      return;
    }

    if (this.isImportingSearchResult) {
      this.messageService.add({
        severity: 'warn',
        summary: 'SDS',
        detail: 'Wait until the selected SDS file has finished importing.'
      });
      return;
    }

    const pictogramIds = this.getSelectedPictogramIds();
    if (this.selectedPictograms.length > 0 && pictogramIds.length !== this.selectedPictograms.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'SDS',
        detail: this.pictogramCatalogLoaded
          ? 'Some selected pictograms could not be matched to backend ids yet.'
          : 'The pictogram catalog has not loaded from the backend yet.'
      });
      return;
    }

    this.loading = true;

    try {
      const payload = this.buildCreateDocumentPayload(pictogramIds);
      const createdDocument = await this.sdsApiService.createDocument(payload).toPromise();
      const documentId = createdDocument.Id;

      if (documentId && this.uploadedFile) {
        await this.sdsApiService.uploadDocumentFile(documentId, this.uploadedFile).toPromise();
      } else if (documentId && this.selectedArchiveFile) {
        await this.sdsApiService.addDocumentFileFromArchive(documentId, this.selectedArchiveFile.id).toPromise();
      }

      // When this SDS is being created AS a replacement for another chemical,
      // link them now (this also archives the original) before navigating.
      let linkedAsReplacement = false;
      if (documentId && this.replacesDocumentId) {
        try {
          await this.sdsApiService
            .assignReplacement(BaseServices.BusinessId, this.replacesDocumentId, documentId, null)
            .toPromise();
          linkedAsReplacement = true;
        } catch (_) {
          this.messageService.add({
            severity: 'warn',
            summary: 'SDS',
            detail: this.translate.instant('SDS_REPLACEMENT_LINK_FAILED')
          });
        }
      }

      this.loading = false;

      this.translate.get('SDS_SAVE_SUCCESS', { name: this.step1Form.value.ProductName }).subscribe(message => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('SAVE'),
          detail: message
        });
      });

      setTimeout(() => {
        // After a replacement-create, open the new chemical's detail so the user
        // sees it is now a replacement; otherwise land on the list (newest first).
        if (linkedAsReplacement && documentId) {
          this.router.navigate(['../detail', documentId], { relativeTo: this.route });
          return;
        }
        this.router.navigate(['../list'], { relativeTo: this.route });
      }, 1200);
    } catch (_) {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('SAVE'),
        detail: 'Could not save the safety data sheet.'
      });
    }
  }

  private buildCreateDocumentPayload(pictogramIds: number[]): SdsCreateDocumentRequest {
    const step1 = this.step1Form.value;
    const step2 = this.step2Form.value;
    const step3 = this.step3Form.value;

    const selectedHazardStatements = (step2.HazardStatements || []) as SdsHazardStatementDto[];

    return {
      BusinessId: BaseServices.BusinessId,
      Product: step1.ProductName,
      CASNumber: step1.NoCasNumber ? '' : joinCasNumbers(step1.CASNumbers as string[]),
      NoCasNumber: step1.NoCasNumber === true,
      Supplier: step1.Supplier,
      HazardStatementCodes: selectedHazardStatements.map(statement => statement.Code),
      Risk: step2.KeyRisk || null,
      Exposure: step2.Description || null,
      Control: step3.OtherControls || null,
      Usage: step2.TaskActivity || null,
      Status: this.getRiskStatusLabel(),
      Probability: this.probabilityValue,
      Consequence: this.consequenceValue,
      RequiresExposureTracking: this.requiresExposureTracking,
      HazardCategory: this.hazardCategoryValue,
      EmployeeExposures: this.requiresExposureTracking && this.canAccessEmployeeExposureData
        ? this.sdsApiService.mapEmployeeExposuresToDto(this.employeeExposures.value)
        : null,
      PictogramIds: pictogramIds
    };
  }

  private getSelectedPictogramIds(): number[] {
    const ids = this.selectedPictograms
      .map(id => this.pictogramList.find(option => option.id === id)?.backendId || null)
      .filter((id): id is number => !!id);

    return [...new Set(ids)];
  }

  private getRiskStatusLabel(): string {
    switch (this.riskData?.value) {
      case 'LOW':
        return 'Low';
      case 'MEDIUM':
        return 'Medium';
      case 'HIGH':
        return 'High';
      default:
        return '';
    }
  }

  downloadPdf(): void {
    if (this.uploadedFile) {
      const url = URL.createObjectURL(this.uploadedFile);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.uploadedFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }

  goBack(): void {
    this.location.back();
  }
}
